import type { JSX } from "react";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { HtmlContent } from "../HtmlContent/HtmlContent";

type Props = {
  id?: string;
  body: JSX.Element | string;
  highlight?: boolean;
  index: number;
  open?: number;
  setOpen: (value: number | undefined) => void;
  title: string;
};

export function FAQItem({
  body,
  highlight,
  id,
  index,
  open,
  setOpen,
  title,
}: Props) {
  const isOpen = open === index;

  return (
    <div
      className={cnTwMerge(
        "w-full cursor-pointer transition-colors duration-300",
        {
          "bg-background/brand/opacity rounded-default": highlight,
        },
      )}
      id={id}
    >
      <div className="p-4">
        <div
          className="flex w-full items-start justify-between"
          onClick={() => setOpen(!isOpen ? index : undefined)}
        >
          <div className="text-text/primary body-base-medium">{title}</div>
          <div className="ml-6 shrink-0 text-sm relative">
            <CombinedIcon
              className="w-6 h-6 text-brand-default"
              isOpen={isOpen}
            />
          </div>
        </div>
        <div
          className={cnTwMerge(
            "body-base-regular overflow-hidden pt-2 text-text/secondary transition-all duration-500",
            {
              "max-h-[500px] opacity-100": isOpen,
              "max-h-0 opacity-0": !isOpen,
            },
          )}
        >
          <HtmlContent content={body} />
        </div>
      </div>
    </div>
  );
}

const CombinedIcon = ({ className = "", isOpen: isMinus = false }) => {
  const minusStyle = {
    transform: isMinus ? "rotate(0deg)" : "rotate(90deg)",
    transition: "transform 0.3s ease-in-out", // Smooth transition
    transformOrigin: "center", // Rotate around the center
  };

  const plusStyle = {
    transform: isMinus ? "rotate(0deg)" : "rotate(90deg)",
    opacity: isMinus ? 0 : 1,
    transition: "all 0.3s ease-in-out ",
    transformOrigin: "center",
  };

  return (
    <svg
      className={className}
      fill="none"
      height="20"
      style={{ cursor: "pointer" }}
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_3288_12073)">
        <path
          d="M6.66699 9.99996H13.3337M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          style={minusStyle}
        />
      </g>
      <g clipPath="url(#clip0_3288_12075)">
        <path
          d="M10.0003 6.66663V13.3333M6.66699 9.99996H13.3337M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          style={plusStyle}
        />
      </g>
      <defs>
        <clipPath id="clip0_3288_12073">
          <rect fill="white" height="20" width="20" />
        </clipPath>
        <clipPath id="clip0_3288_12075">
          <rect fill="white" height="20" width="20" />
        </clipPath>
      </defs>
    </svg>
  );
};
